import React from 'react';
import { notification } from 'antd';
import AddTrackedWebsiteWizard from './wizard/AddTrackedWebsiteWizard';
import { AddTrackedWebsitesParams } from './types';
import { useAddTrackedKeywordsMutation } from '../trackedKeywords/service';
import { useAddWebsiteIndexEntryMutation } from '../deepAnalysis/websiteIndexEntry/service';
import { useAddSitemapImportProcessMutation, useAddDomainAnalysisProcessMutation } from '../deepAnalysis/process/service';
import { useCreateBackgroundAnalysisReportMutation } from '../analysis/service';
import { useSendOnboardingEventMutation } from './service';
import useGetCurrentWebsite from '../deepAnalysis/websiteIndexEntry/hooks/useGetCurrentWebsite';



type Props = {
    fromOnboarding: boolean;
    onFinishAddedWebsite: () => void;
};


export default function AddNewTrackedWebsite({ fromOnboarding, onFinishAddedWebsite }: Props) {
    const { setSelectedWebsite } = useGetCurrentWebsite();
    const [addWebsiteIndexEntry] = useAddWebsiteIndexEntryMutation();
    const [addTrackedKeywords] = useAddTrackedKeywordsMutation();
    const [addSitemapImportProcess] = useAddSitemapImportProcessMutation();
    const [addDomainAnalysisProcess] = useAddDomainAnalysisProcessMutation();
    const [createBackgroundAnalysis] = useCreateBackgroundAnalysisReportMutation();
    const [sendOnboardingEvent] = useSendOnboardingEventMutation();

    const handleAddTrackedWebsite = async (params: AddTrackedWebsitesParams, keywords: string[]) => {
        try {
            let newWebsiteIndexEntry;

            try {
                newWebsiteIndexEntry = await addWebsiteIndexEntry({ domain: params.domain, homepage_url: params.homepage, settings: params.settings }).unwrap();
            } catch (error) {
                notification.error({
                    message: `We cannot access ${params.domain} in order to perform our test!`,
                    description: 'Either the domain is not online, or our tool is being blocked by your server.',
                });
                return;
            }

            if (newWebsiteIndexEntry) {
                try {
                    // start sitemap import
                    if (newWebsiteIndexEntry.sitemap_urls.length > 0) {
                        await addSitemapImportProcess({ w_guid: newWebsiteIndexEntry.guid, sitemap_urls: newWebsiteIndexEntry.sitemap_urls }).unwrap();
                    }

                    // start site-wide factors analysis
                    await addDomainAnalysisProcess({ w_guid: newWebsiteIndexEntry.guid }).unwrap();

                    // start a full legacy seo-audit
                    const source = fromOnboarding ? 'onboarding_wizard' : 'add_tracked_website_wizard';
                    const backgroundAnalysisReport = await createBackgroundAnalysis({ url: params.homepage, competitors: params.settings.competitors, source }).unwrap();

                    // send finish_onboarding tracking event only if background analysis was initiated from the onboarding wizard
                    if (fromOnboarding) {
                        const onboardingEvent = {
                            main_url: params.homepage,
                            report_guid: backgroundAnalysisReport.guid,
                            location_name: params.settings.location_name,
                            language_code: params.settings.language_code,
                        };

                        await sendOnboardingEvent({ event_type: 'finish_onboarding', meta: onboardingEvent }).unwrap();
                    }

                    // add tracked keywords
                    await addTrackedKeywords({
                        domain: params.domain,
                        locationName: params.settings.location_name,
                        languageCode: params.settings.language_code,
                        keywords: keywords,
                    }).unwrap();

                    // set the selected website in the context
                    // const newTrackedWebsite = {
                    //     guid: newWebsiteIndexEntry.guid,
                    //     url: newWebsiteIndexEntry.homepage_url,
                    //     domain: newWebsiteIndexEntry.domain,
                    //     c_at: newWebsiteIndexEntry.c_at,
                    //     crawled_at: [],
                    //     settings: {
                    //         location_name: params.settings.location_name,
                    //         language_code: params.settings.language_code,
                    //         competitors: params.settings.competitors,
                    //         regions: params.settings.regions,
                    //         notifications: params.settings.notifications || [],
                    //     }
                    // }
                    setSelectedWebsite(newWebsiteIndexEntry.guid);
                } catch (_) {
                    notification.error({ message: 'Something went wrong!', description: 'Please try again or contact support!' });
                }
            }
        } catch (error) {
        } finally {
            onFinishAddedWebsite();
        }
    };

    return (
        <AddTrackedWebsiteWizard
            fromOnboarding={fromOnboarding}
            onAddTrackedWebsite={handleAddTrackedWebsite}
        />
    );
}
